import { lazy } from 'react'

import { authRoles } from '../../../auth/authRoles'
import Loadable from '../../base/components/Template/Loadable/Loadable.tsx'
import { FormModeProvider } from '../../base/contexts/FormModeContext.tsx'
import { OperacionesProvider } from '../../base/contexts/OperacionesContext'
import { EntradaInputProp } from './interfaces'
import {
  EntradaFormMode,
  entradaFormModeDefault,
  entradaFormModelConfig,
} from './services/entradaFormMode.ts'

const EntradaSolicitar = Loadable(lazy(() => import('./view/EntradaSolicitar.tsx')))
const EntradaAprobar = Loadable(lazy(() => import('./view/EntradaAprobar.tsx')))
const EntradaRecepcionar = Loadable(lazy(() => import('./view/EntradaRecepcionar.tsx')))
const EntradaConsultar = Loadable(lazy(() => import('./view/EntradaConsultar.tsx')))
const EntradaRapidaGestion = Loadable(lazy(() => import('./view/EntradaRapidaGestion.tsx')))
const EntradaSolicitarRegistroApp = Loadable(lazy(() => import('./view/EntradaRegistro')))
const EntradaConsultarFormApp = Loadable(lazy(() => import('./view/EntradaConsultarFormulario')))
const EntradaRecepcionRegistroApp = Loadable(lazy(() => import('./view/EntradaRecepcionRegistro.tsx')))
const EntradaRecepcionParcial = Loadable(lazy(() => import('./view/EntradaRecepcionParcial.tsx')))
const EntradaActualizarApp = Loadable(lazy(() => import('./view/EntradaActualizar')))
const EntradaRapidaRegistroApp = Loadable(lazy(() => import('./view/EntradaRapidaRegistro')))

export const entradaRoutesMap = {
  solicitar: {
    path: '/entrada/solicitar',
    name: 'Solicitar',
    action: 'SOLICITAR',
  },
  aprobar: {
    path: '/entrada/aprobar',
    name: 'Aprobar',
    action: 'APROBAR',
  },
  recepcionar: {
    path: '/entrada/recepcionar',
    name: 'Recepcionar',
    action: 'RECEPCIONAR',
  },
  recepcionParcial: {
    path: '/entrada/recepcion-parcial',
    name: 'Recepción Parcial',
    action: 'RECEPCIONAR',
  },
  entradaRapidaGestion: {
    path: '/entrada-rapida/gestion',
    name: 'Entradas Rapidas',
    action: 'REGISTRO_RAPIDO',
  },
  entradaRapidaRegistro: {
    path: '/entrada-rapida/rapida',
    name: ' Registro Rápido',
    action: 'REGISTRO_RAPIDO',
  },
  actualizar: {
    path: '/entrada/actualizar',
    name: 'Actualizar entrada',
    action: 'ADMINISTRAR',
  },
  registro: {
    path: '/entrada/nuevo',
    name: 'Nueva Entrada',
    action: 'ADMINISTRAR',
  },
  recepcion: {
    path: '/entrada/recepcion',
    name: 'Recepción',
    action: 'RECEPCION',
  },
  recepcionRegistro: {
    path: '/entrada/recepcion/:id',
    name: 'Registro recepción entrada',
    action1: 'REGISTRAR_RECEPCION',
    action2: 'FINALIZAR_ENTRADA',
    action3: 'ACTUALIZAR_RECEPCION',
    action4: 'SOLICITAR_RECEPCION',
    action5: 'ANULAR_RECEPCION',
    action6: 'OBSERVAR_RECEPCION',
    action7: 'APROBAR_RECEPCION',
  },
  aprobarRegistro: {
    path: '/entrada/aprobarRegistro/:id',
    name: 'Aprobar Entrada',
    action: 'ADMINISTRAR',
  },
  consultar: {
    path: '/entrada/consultar',
    name: 'Consultar',
    action: 'CONSULTAR',
  },
  consultaForm: {
    path: '/entrada/consultar/:id',
    name: 'Consulta detallada',
    action: 'CONSULTAR',
  },
}

/**
 * Estructura de ruta para cargar la página principal
 */
const entradaRoutes = [
  {
    path: entradaRoutesMap.solicitar.path,
    element: (
      <OperacionesProvider>
        <EntradaSolicitar />
      </OperacionesProvider>
    ),
    auth: authRoles.admin,
  },
  {
    path: entradaRoutesMap.aprobar.path,
    element: (
      <OperacionesProvider>
        <EntradaAprobar />
      </OperacionesProvider>
    ),
    auth: authRoles.admin,
  },
  {
    path: entradaRoutesMap.recepcionar.path,
    element: (
      <OperacionesProvider>
        <EntradaRecepcionar />
      </OperacionesProvider>
    ),
    auth: authRoles.admin,
  },
  {
    path: entradaRoutesMap.recepcionParcial.path,
    element: (
      <OperacionesProvider>
        <EntradaRecepcionParcial />
      </OperacionesProvider>
    ),
    auth: authRoles.admin,
  },
  {
    path: entradaRoutesMap.consultar.path,
    element: (
      <OperacionesProvider>
        <EntradaConsultar />
      </OperacionesProvider>
    ),
    auth: authRoles.admin,
  },
  {
    path: entradaRoutesMap.entradaRapidaGestion.path,
    element: (
      <OperacionesProvider>
        <EntradaRapidaGestion />
      </OperacionesProvider>
    ),
    auth: authRoles.admin,
  },
  {
    path: entradaRoutesMap.entradaRapidaRegistro.path,
    element: (
      <FormModeProvider<EntradaInputProp, EntradaFormMode>
        mode={'rapid'}
        config={entradaFormModelConfig}
        defaults={entradaFormModeDefault}
      >
        <OperacionesProvider>
          <EntradaRapidaRegistroApp />
        </OperacionesProvider>
      </FormModeProvider>
    ),
    auth: authRoles.admin,
  },
  {
    path: entradaRoutesMap.registro.path,
    element: (
      <OperacionesProvider>
        <EntradaSolicitarRegistroApp />
      </OperacionesProvider>
    ),
    auth: authRoles.admin,
  },
  {
    path: `${entradaRoutesMap.actualizar.path}/:id`,
    element: (
      <OperacionesProvider>
        <EntradaActualizarApp />
      </OperacionesProvider>
    ),
    auth: authRoles.admin,
  },
  {
    path: entradaRoutesMap.recepcionRegistro.path,
    element: (
      <OperacionesProvider>
        <EntradaRecepcionRegistroApp />
      </OperacionesProvider>
    ),
    auth: authRoles.admin,
  },
  {
    path: entradaRoutesMap.consultaForm.path,
    element: (
      <OperacionesProvider>
        <EntradaConsultarFormApp />
      </OperacionesProvider>
    ),
    auth: authRoles.admin,
  },
]

export default entradaRoutes
